import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Network,
  Plus,
  Search,
  Trash2,
  Upload,
  UserCheck,
  X,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Dashboard proxy.
 *
 * Seluruh crawl dulu keluar dari satu alamat IP milik server. Situs yang
 * menghitung permintaan per IP melihat seluruh pelanggan Crawel sebagai satu
 * pengunjung yang sangat sibuk, lalu memblokirnya - dan sesudah itu SEMUA
 * pelanggan kehilangan situs tersebut sekaligus, bukan cuma yang
 * menyebabkannya.
 *
 * Dua jenis, dan bedanya komersial bukan teknis:
 *   kolam  dibagi ramai-ramai oleh sebuah paket, berputar tiap permintaan
 *   khusus dipegang satu ruang kerja, dibeli atau ditugaskan dari sini
 *
 * Yang khusus menang atas yang kolam. Pelanggan yang membayar untuk alamat
 * yang tidak dibagi siapa pun tidak boleh diam-diam dilempar ke kolam bersama.
 */

const PAKET = ['free', 'starter', 'growth', 'pro', 'business'];

const NADA_SEHAT = { sehat: 'success', gagal: 'danger', belum: 'neutral' };
const LABEL_SEHAT = { sehat: 'Sehat', gagal: 'Gagal', belum: 'Belum diuji' };

const kosong = {
  label: '',
  protokol: 'http',
  host: '',
  port: '',
  username: '',
  password: '',
  jenis: 'kolam',
  planIds: [],
  grup: '',
  catatan: '',
  aktif: true,
};

// -- Formulir satu proxy --------------------------------------------------

const FormProxy = ({ awal, onSimpan, menyimpan, onBatal }) => {
  const [f, setF] = useState({ ...kosong, ...awal, password: '' });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const ubah = (k) => (e) => set(k)(e.target.value);

  const togglePaket = (id) =>
    set('planIds')(
      f.planIds.includes(id) ? f.planIds.filter((p) => p !== id) : [...f.planIds, id],
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Label" hint="Nama yang Anda baca, misalnya Jakarta 01">
          {(p) => <Input {...p} value={f.label} onChange={ubah('label')} />}
        </Field>
        <Field label="Protokol">
          {(p) => (
            <Select {...p} value={f.protokol} onChange={ubah('protokol')}>
              <option value="http">HTTP</option>
              <option value="https">HTTPS</option>
              <option value="socks5">SOCKS5</option>
              <option value="socks5h">SOCKS5h (DNS lewat proxy)</option>
            </Select>
          )}
        </Field>
        <Field label="Host">
          {(p) => <Input {...p} value={f.host} onChange={ubah('host')} placeholder="10.0.0.1" />}
        </Field>
        <Field label="Port">
          {(p) => <Input {...p} type="number" value={f.port} onChange={ubah('port')} />}
        </Field>
        <Field label="Pengguna" hint="Kosongkan kalau proxynya tanpa autentikasi">
          {(p) => <Input {...p} value={f.username} onChange={ubah('username')} />}
        </Field>
        <Field
          label="Kata sandi"
          hint={awal?.id ? 'Kosongkan kalau tidak ingin menggantinya.' : undefined}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={f.password}
              onChange={ubah('password')}
              placeholder={awal?.adaSandi ? 'Tersimpan, biarkan kosong' : ''}
            />
          )}
        </Field>
      </div>

      <Field label="Jenis" hint="Kolam dibagi satu paket, khusus dipegang satu ruang kerja">
        {(p) => (
          <Select {...p} value={f.jenis} onChange={ubah('jenis')}>
            <option value="kolam">Kolam berputar - untuk paket</option>
            <option value="khusus">Khusus - untuk satu ruang kerja</option>
          </Select>
        )}
      </Field>

      {f.jenis === 'kolam' ? (
        <div>
          <p className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
            Paket yang memakainya
          </p>
          <div className="flex flex-wrap gap-2">
            {PAKET.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => togglePaket(id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors',
                  f.planIds.includes(id)
                    ? 'bg-brand-950 text-white dark:bg-accent-600'
                    : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                )}
              >
                {id}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-[var(--text-muted)]">
            Tanpa satu pun paket dipilih, proxy ini masuk stok tetapi belum dipakai siapa-siapa.
          </p>
        </div>
      ) : (
        <p className="rounded-lg bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
          Proxy khusus yang belum ditugaskan menjadi stok siap jual. Saat ada yang membeli add-on
          proxy, stok inilah yang otomatis diberikan. Anda juga bisa menugaskannya manual dari
          tombol Tugaskan di daftar, dan satu proxy khusus boleh dipegang beberapa ruang kerja
          sekaligus, bukan hanya satu.
        </p>
      )}

      <Field
        label="Grup"
        hint="Untuk mengelompokkan, mis. nama penyedia atau kotanya. Boleh dikosongkan."
      >
        {(p) => (
          <Input {...p} value={f.grup} onChange={ubah('grup')} placeholder="Penyedia A - Jakarta" list="daftar-grup-proxy" />
        )}
      </Field>

      <Field label="Catatan" hint="Penyedia, tanggal berlaku, atau apa pun yang perlu diingat">
        {(p) => <Textarea {...p} rows={2} value={f.catatan} onChange={ubah('catatan')} />}
      </Field>

      <Toggle
        checked={f.aktif}
        onChange={set('aktif')}
        label="Aktif"
        description="Kalau dimatikan, proxy tetap tersimpan tetapi tidak dipakai eksekusi mana pun."
      />

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBatal}>
          Batal
        </Button>
        <Button
          loading={menyimpan}
          disabled={!f.label || !f.host || !f.port}
          onClick={() => onSimpan({ ...f, port: Number(f.port) })}
        >
          Simpan
        </Button>
      </div>
    </div>
  );
};

// -- Tempel massal --------------------------------------------------------

const FormMassal = ({ onSimpan, menyimpan, onBatal }) => {
  const [f, setF] = useState({ teks: '', jenis: 'kolam', planIds: [], aktif: true });

  return (
    <div className="space-y-4">
      <Field
        label="Tempel daftar proxy"
        hint="Satu per baris. Baris yang diawali # dilewati."
      >
        {(p) => (
          <Textarea
            {...p}
            rows={10}
            className="font-mono text-xs"
            value={f.teks}
            onChange={(e) => setF((s) => ({ ...s, teks: e.target.value }))}
            placeholder={
              '10.0.0.1:8080\n' +
              '10.0.0.2:8080:pengguna:sandi\n' +
              'socks5://10.0.0.3:1080\n' +
              'http://pengguna:sandi@10.0.0.4:3128'
            }
          />
        )}
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Jenis">
          {(p) => (
            <Select
              {...p}
              value={f.jenis}
              onChange={(e) => setF((s) => ({ ...s, jenis: e.target.value }))}
            >
              <option value="kolam">Kolam berputar</option>
              <option value="khusus">Khusus (stok siap jual)</option>
            </Select>
          )}
        </Field>
        {f.jenis === 'kolam' && (
          <Field label="Paket yang memakainya">
            {() => (
              <div className="flex flex-wrap gap-1.5 pt-1.5">
                {PAKET.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      setF((s) => ({
                        ...s,
                        planIds: s.planIds.includes(id)
                          ? s.planIds.filter((p) => p !== id)
                          : [...s.planIds, id],
                      }))
                    }
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                      f.planIds.includes(id)
                        ? 'bg-brand-950 text-white dark:bg-accent-600'
                        : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
                    )}
                  >
                    {id}
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}
      </div>

      <p className="text-xs text-[var(--text-muted)]">
        Baris yang tidak terbaca dilewati, bukan membatalkan seluruh unggahan. Yang gagal
        dilaporkan beserta nomor barisnya supaya bisa diperbaiki, bukan ditebak.
      </p>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBatal}>
          Batal
        </Button>
        <Button loading={menyimpan} disabled={!f.teks.trim()} onClick={() => onSimpan(f)}>
          Tambahkan
        </Button>
      </div>
    </div>
  );
};

// -- Halaman --------------------------------------------------------------

const ProxiesAdminPage = () => {
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [jenis, setJenis] = useState('');
  const [grup, setGrup] = useState('');
  const [formBuka, setFormBuka] = useState(null);
  const [massalBuka, setMassalBuka] = useState(false);
  const [tugaskanBuka, setTugaskanBuka] = useState(null);
  const [hapusBuka, setHapusBuka] = useState(null);
  const [memeriksa, setMemeriksa] = useState(null);

  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (jenis) params.set('jenis', jenis);
  if (grup) params.set('grup', grup);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'proxies', params.toString()],
    queryFn: () => api.get(`/platform/proxies?${params.toString()}`),
  });

  const { data: ringkas } = useQuery({
    queryKey: ['platform', 'proxies', 'summary'],
    queryFn: () => api.get('/platform/proxies/summary'),
  });

  const { data: dataGrup } = useQuery({
    queryKey: ['platform', 'proxies', 'groups'],
    queryFn: () => api.get('/platform/proxies/groups'),
  });
  const grupTersedia = dataGrup?.data ?? [];

  const segarkan = () => qc.invalidateQueries({ queryKey: ['platform', 'proxies'] });

  const galat = (err) =>
    toast.error('Gagal', {
      description: err instanceof ApiError ? err.message : 'Coba lagi.',
    });

  const simpan = useMutation({
    mutationFn: (body) =>
      body.id
        ? api.patch(`/platform/proxies/${body.id}`, body)
        : api.post('/platform/proxies', body),
    onSuccess: () => {
      toast.success('Tersimpan');
      setFormBuka(null);
      segarkan();
    },
    onError: galat,
  });

  const massal = useMutation({
    mutationFn: (body) => api.post('/platform/proxies/bulk', body),
    onSuccess: (res) => {
      const d = res?.data;
      toast.success(`${d.dibuat} proxy ditambahkan`, {
        description: d.gagal?.length
          ? `${d.gagal.length} baris dilewati: ${d.gagal
              .slice(0, 3)
              .map((g) => `baris ${g.baris}`)
              .join(', ')}`
          : undefined,
      });
      setMassalBuka(false);
      segarkan();
    },
    onError: galat,
  });

  const periksa = useMutation({
    mutationFn: (id) => api.post(`/platform/proxies/${id}/check`),
    onMutate: (id) => setMemeriksa(id),
    onSettled: () => setMemeriksa(null),
    onSuccess: (res) => {
      const k = res?.data?.kesehatan;
      if (k?.status === 'sehat') {
        toast.success(`Sehat, ${k.latensiMs} ms`, {
          description: k.ipTerlihat ? `Situs tujuan melihat IP ${k.ipTerlihat}` : undefined,
        });
      } else {
        toast.error('Proxy tidak bisa dipakai', { description: k?.pesan });
      }
      segarkan();
    },
    onError: galat,
  });

  const periksaSemua = useMutation({
    mutationFn: () => api.post('/platform/proxies/check-all', {}),
    onSuccess: (res) => {
      const d = res?.data;
      toast.success(`${d.diperiksa} proxy diperiksa`, {
        description: `${d.sehat} sehat, ${d.gagal} gagal.`,
      });
      segarkan();
    },
    onError: galat,
  });

  const tugaskan = useMutation({
    mutationFn: ({ id, organizationIds }) =>
      api.patch(`/platform/proxies/${id}/assign`, { organizationIds }),
    onSuccess: () => {
      toast.success('Penugasan diperbarui');
      setTugaskanBuka(null);
      segarkan();
    },
    onError: galat,
  });

  const hapus = useMutation({
    mutationFn: (id) => api.del(`/platform/proxies/${id}`),
    onSuccess: () => {
      toast.success('Proxy dihapus');
      setHapusBuka(null);
      segarkan();
    },
    onError: galat,
  });

  if (isLoading) return <PageLoader label="Memuat proxy..." />;

  const items = data?.data ?? [];
  const r = ringkas?.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Proxy</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Tanpa proxy, seluruh crawl keluar dari satu alamat IP server. Situs yang membatasi
            permintaan per IP akan melihat semua pelanggan sebagai satu pengunjung yang sangat
            sibuk, lalu memblokirnya untuk semua orang sekaligus.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={Upload} onClick={() => setMassalBuka(true)}>
            Tempel Daftar
          </Button>
          <Button icon={Plus} onClick={() => setFormBuka({})}>
            Tambah
          </Button>
        </div>
      </div>

      {r && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Total proxy', nilai: formatNumber(r.total ?? 0), icon: Network },
            { label: 'Kolam berputar', nilai: formatNumber(r.perJenis?.kolam ?? 0), icon: Layers },
            {
              label: 'Khusus',
              nilai: formatNumber(r.perJenis?.khusus ?? 0),
              icon: UserCheck,
            },
            {
              label: 'Stok siap jual',
              nilai: formatNumber(r.stokKhususTersedia ?? 0),
              icon: CheckCircle2,
              // Stok nol berarti pembelian add-on proxy berikutnya akan
              // tertunda menunggu Anda menambah stok.
              nada: (r.stokKhususTersedia ?? 0) === 0 ? 'text-warning-600' : undefined,
            },
          ].map((k) => (
            <Card key={k.label} className="p-4">
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <k.icon className="size-3.5" aria-hidden="true" />
                {k.label}
              </div>
              <p
                className={cn(
                  'mt-1 text-2xl font-extrabold',
                  k.nada ?? 'text-[var(--text-primary)]',
                )}
              >
                {k.nilai}
              </p>
            </Card>
          ))}
        </div>
      )}

      {(r?.stokKhususTersedia ?? 1) === 0 && (r?.perJenis?.khusus ?? 0) > 0 && (
        <p className="flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-xs text-warning-700 dark:bg-warning-500/10 dark:text-warning-500">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          Stok proxy khusus habis. Pembelian add-on proxy berikutnya tetap diterima dan uangnya
          masuk, tetapi alamatnya baru bisa ditugaskan sesudah Anda menambah stok.
        </p>
      )}

      <Card>
        <CardHeader
          title="Daftar proxy"
          action={
            <Button
              size="sm"
              variant="secondary"
              icon={Activity}
              loading={periksaSemua.isPending}
              onClick={() => periksaSemua.mutate()}
            >
              Periksa Semua
            </Button>
          }
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--text-muted)]"
                aria-hidden="true"
              />
              <Input
                placeholder="Cari label atau host"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={jenis}
              onChange={(e) => setJenis(e.target.value)}
              className="max-w-[180px]"
            >
              <option value="">Semua jenis</option>
              <option value="kolam">Kolam berputar</option>
              <option value="khusus">Khusus</option>
            </Select>

            <Select
              value={grup}
              onChange={(e) => setGrup(e.target.value)}
              className="max-w-[200px]"
            >
              <option value="">Semua grup</option>
              {grupTersedia
                .filter((g) => g.nama)
                .map((g) => (
                  <option key={g.nama} value={g.nama}>
                    {g.nama} ({g.jumlah})
                  </option>
                ))}
              {grupTersedia.some((g) => !g.nama) && (
                <option value="-">
                  Belum dikelompokkan ({grupTersedia.find((g) => !g.nama)?.jumlah ?? 0})
                </option>
              )}
            </Select>
          </div>

          {/* Daftar grup yang sudah dipakai, supaya menulisnya di formulir
              tidak perlu mengingat ejaan yang persis sama. */}
          <datalist id="daftar-grup-proxy">
            {grupTersedia.filter((g) => g.nama).map((g) => (
              <option key={g.nama} value={g.nama} />
            ))}
          </datalist>

          {items.length === 0 ? (
            <EmptyState
              icon={Network}
              title="Belum ada proxy"
              description="Tambahkan satu per satu, atau tempel daftar dari penyedia Anda sekaligus."
            />
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Proxy</Th>
                    <Th>Jenis</Th>
                    <Th>Dipakai</Th>
                    <Th>Kesehatan</Th>
                    <Th align="right">Pemakaian</Th>
                    <Th align="right">Tindakan</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <Tr key={p.id}>
                      <Td>
                        <p className="font-semibold text-[var(--text-primary)]">{p.label}</p>
                        <p className="font-mono text-xs text-[var(--text-muted)]">
                          {p.url}
                          {p.adaSandi && ' (berautentikasi)'}
                        </p>
                        {p.grup && (
                          <Badge tone="neutral" size="sm" className="mt-1">
                            {p.grup}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Badge size="sm" tone={p.jenis === 'khusus' ? 'brand' : 'info'}>
                          {p.jenis === 'khusus' ? 'Khusus' : 'Kolam'}
                        </Badge>
                        {!p.aktif && (
                          <Badge size="sm" tone="neutral" className="ml-1">
                            Nonaktif
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        {p.jenis === 'kolam' ? (
                          p.planIds?.length ? (
                            <span className="text-xs capitalize">{p.planIds.join(', ')}</span>
                          ) : (
                            <span className="text-xs text-[var(--text-muted)]">
                              Belum ditugaskan
                            </span>
                          )
                        ) : p.organisasi?.length ? (
                          <span className="text-xs">
                            {p.organisasi.map((o) => o.nama).join(', ')}
                            {p.organisasi.length > 1 && (
                              <span className="text-[var(--text-muted)]">
                                {' '}
                                ({p.organisasi.length} ruang kerja)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">Stok siap jual</span>
                        )}
                      </Td>
                      <Td>
                        <Badge size="sm" tone={NADA_SEHAT[p.kesehatan?.status] ?? 'neutral'}>
                          {LABEL_SEHAT[p.kesehatan?.status] ?? 'Belum diuji'}
                        </Badge>
                        {p.kesehatan?.diperiksaPada && (
                          <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                            {p.kesehatan.status === 'sehat'
                              ? `${p.kesehatan.latensiMs} ms`
                              : p.kesehatan.pesan?.slice(0, 40)}
                          </p>
                        )}
                      </Td>
                      <Td align="right">
                        <span className="text-xs">
                          {formatNumber(p.statistik?.dipakai ?? 0)} kali
                        </span>
                        {(p.statistik?.gagal ?? 0) > 0 && (
                          <p className="text-[11px] text-danger-600">
                            {formatNumber(p.statistik.gagal)} gagal
                          </p>
                        )}
                        {p.statistik?.terakhirDipakai && (
                          <p className="text-[11px] text-[var(--text-muted)]">
                            {formatDateTime(p.statistik.terakhirDipakai)}
                          </p>
                        )}
                      </Td>
                      <Td align="right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="xs"
                            variant="ghost"
                            icon={Activity}
                            loading={memeriksa === p.id}
                            onClick={() => periksa.mutate(p.id)}
                          >
                            Uji
                          </Button>
                          {p.jenis === 'khusus' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              icon={UserCheck}
                              onClick={() => setTugaskanBuka(p)}
                            >
                              Tugaskan
                            </Button>
                          )}
                          <Button size="xs" variant="ghost" onClick={() => setFormBuka(p)}>
                            Ubah
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            icon={Trash2}
                            iconOnly
                            aria-label={`Hapus ${p.label}`}
                            onClick={() => setHapusBuka(p)}
                          />
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      <Modal
        open={Boolean(formBuka)}
        onClose={() => setFormBuka(null)}
        size="lg"
        title={formBuka?.id ? `Ubah ${formBuka.label}` : 'Tambah proxy'}
      >
        {formBuka && (
          <FormProxy
            awal={formBuka}
            menyimpan={simpan.isPending}
            onBatal={() => setFormBuka(null)}
            onSimpan={(body) => simpan.mutate({ ...body, id: formBuka.id })}
          />
        )}
      </Modal>

      <Modal
        open={massalBuka}
        onClose={() => setMassalBuka(false)}
        size="lg"
        title="Tempel daftar proxy"
      >
        <FormMassal
          menyimpan={massal.isPending}
          onBatal={() => setMassalBuka(false)}
          onSimpan={(body) => massal.mutate(body)}
        />
      </Modal>

      <TugaskanDialog
        proxy={tugaskanBuka}
        onClose={() => setTugaskanBuka(null)}
        onSimpan={(organizationIds) =>
          tugaskan.mutate({ id: tugaskanBuka.id, organizationIds })
        }
        menyimpan={tugaskan.isPending}
      />

      <ConfirmDialog
        open={Boolean(hapusBuka)}
        onClose={() => setHapusBuka(null)}
        onConfirm={() => hapus.mutate(hapusBuka.id)}
        loading={hapus.isPending}
        destructive
        title={`Hapus ${hapusBuka?.label}?`}
        confirmLabel="Hapus"
        description="Eksekusi yang sedang berjalan tetap memakainya sampai selesai. Sesudah itu, proxy ini tidak akan dipakai lagi."
      />
    </div>
  );
};

/**
 * Dialog penugasan proxy khusus.
 *
 * Menerima BEBERAPA ruang kerja sekaligus. Proxy khusus tidak selalu berarti
 * eksklusif untuk satu pelanggan: beberapa ruang kerja milik satu perusahaan
 * sering perlu berbagi alamat yang sama.
 *
 * Pilihannya disimpan sementara di dialog dan baru dikirim saat ditekan
 * Simpan, bukan seketika tiap kali dicentang. Menugaskan satu per satu dengan
 * satu permintaan masing-masing membuat keadaan setengah jadi kalau salah
 * satunya gagal, dan tidak ada cara membatalkannya.
 */
const TugaskanDialog = ({ proxy, onClose, onSimpan, menyimpan }) => {
  const [cari, setCari] = useState('');
  const [dipilih, setDipilih] = useState([]);

  // Pilihan disetel ulang tiap kali dialognya dibuka untuk proxy yang lain.
  // Tanpa ini, sisa pilihan dari proxy sebelumnya ikut terbawa.
  useEffect(() => {
    setDipilih(proxy?.organizationIds ?? []);
    setCari('');
  }, [proxy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data } = useQuery({
    queryKey: ['platform', 'organizations', 'untuk-proxy', cari],
    queryFn: () =>
      api.get(`/platform/organizations?limit=10${cari ? `&search=${encodeURIComponent(cari)}` : ''}`),
    enabled: Boolean(proxy),
  });

  const orgs = data?.data ?? [];

  const toggle = (id) =>
    setDipilih((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Yang sudah dipilih tetapi tidak muncul di hasil pencarian sekarang tetap
  // harus terlihat, kalau tidak orang mengira pilihannya hilang.
  const namaTerpilih = (id) =>
    proxy?.organisasi?.find((o) => o.id === id)?.nama ??
    orgs.find((o) => o.id === id)?.nama ??
    'Ruang kerja';

  return (
    <Modal
      open={Boolean(proxy)}
      onClose={onClose}
      title={`Tugaskan ${proxy?.label ?? ''}`}
      description={
        proxy?.organisasi?.length
          ? `Sekarang dipegang ${proxy.organisasi.length} ruang kerja.`
          : 'Belum dipegang siapa pun.'
      }
    >
      <div className="space-y-3">
        {dipilih.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {dipilih.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => toggle(id)}
                className="inline-flex items-center gap-1 rounded-full bg-brand-950 px-2.5 py-1 text-xs font-semibold text-white dark:bg-accent-600"
              >
                {namaTerpilih(id)}
                <X className="size-3" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}

        <Input
          placeholder="Cari ruang kerja"
          value={cari}
          onChange={(e) => setCari(e.target.value)}
        />

        <div className="max-h-56 space-y-1 overflow-y-auto">
          {orgs.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm',
                dipilih.includes(o.id)
                  ? 'bg-[var(--surface-sunken)]'
                  : 'hover:bg-[var(--surface-sunken)]',
              )}
            >
              <span>
                <span className="font-semibold text-[var(--text-primary)]">{o.nama}</span>
                <span className="block text-xs text-[var(--text-muted)]">
                  {o.pemilik?.email ?? 'tanpa pemilik'} &middot; paket {o.paket}
                </span>
              </span>
              {dipilih.includes(o.id) && (
                <CheckCircle2 className="size-4 shrink-0 text-success-600" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>

        <p className="text-xs text-[var(--text-muted)]">
          Melepas seluruh penugasan mengembalikan proxy ini menjadi stok siap jual, bukan ke kolam
          bersama. Memindahkan proxy berbayar ke kolam tanpa diminta adalah keputusan komersial,
          bukan keputusan teknis.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          {dipilih.length > 0 && (
            <Button variant="dangerOutline" onClick={() => setDipilih([])}>
              Kosongkan
            </Button>
          )}
          <Button loading={menyimpan} onClick={() => onSimpan(dipilih)}>
            {dipilih.length > 0 ? `Simpan (${dipilih.length})` : 'Lepaskan semua'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ProxiesAdminPage;
